<script setup lang="tsx">
import type { Response, Responsible, ResponsibleFor } from '@/models/api-generated'
import ResponsibleSelect from '../widgets/ResponsibleSelect.vue'
import { computed, ref, useTemplateRef, watch } from 'vue'
import Modal from '../shared/Modal.vue'
import * as bootstrap from 'bootstrap'
import http from '@/services/http'
import ResponsibleForTabs from './ResponsibleForTabs.vue'

const emit = defineEmits<{
	confirm: [replacementResponsible?: Responsible]
	closed: [confirmed: boolean]
}>()
const props = defineProps<{
	responsible: Responsible
}>()
const show = defineModel<boolean>({ required: true })
const tablist = useTemplateRef('tablist')
const responsibleFor = ref<ResponsibleFor>()
const isResponsible = computed(() => {
	return !!(
		responsibleFor.value?.assets.length ||
		responsibleFor.value?.dataAssets.length ||
		responsibleFor.value?.methodMeta.length ||
		responsibleFor.value?.moduleMeta.length ||
		responsibleFor.value?.moduleAssets.length ||
		responsibleFor.value?.businessProcesses.length ||
		responsibleFor.value?.riskShort.length
	)
})
const replacementResponsible = ref<Responsible>()

watch(show, async (isShown) => {
	if (!isShown) return

	replacementResponsible.value = undefined

	const { data } = await http.get<Response<ResponsibleFor>>(`api/responsibleFor/${props.responsible.type}/${props.responsible.id}`)
	responsibleFor.value = data.data

	setTimeout(() => {
		const trig = tablist.value?.querySelector('.nav-item button') as HTMLElement | undefined
		if (trig) {
			const tab = new bootstrap.Tab(trig)
			tab.show()
		}
	})
})
</script>

<template>
	<Modal
		:class="isResponsible ? 'modal-lg' : ''"
		v-model="show"
		@confirm="emit('confirm', replacementResponsible)"
		@closed="emit('closed', $event)"
		type="user"
	>
		<template #header>
			<h5>{{ $t('misc.confirm') }}</h5>
		</template>
		<template #body>
			<div class="row">
				<label class="col-7 col-form-label">{{ $t(props.responsible.type === 'roles' ? 'confirmRoleDelete' : 'confirmUserDelete') }}</label>
				<label class="col-5 col-form-label fw-bold">{{ props.responsible.label }}</label>
			</div>

			<template v-if="isResponsible">
				<div class="row">
					<label class="col-7 col-form-label">{{ $t('replacementResponsible') }}</label>
					<div class="col-5">
						<ResponsibleSelect :hide="[responsible]" :placeholder="$t('misc.nothing')" v-model="replacementResponsible"></ResponsibleSelect>
					</div>
				</div>
				<ResponsibleForTabs :responsible-for="responsibleFor" />
			</template>
		</template>
	</Modal>
</template>

<style lang="sass" scoped>
.tab-content
  max-height: 50vh
  overflow-x: hidden
  overflow-y: auto
</style>
